import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAlerts } from "../contexts/AlertsContext";

const NAV_ITEMS = [
  { to: "/",           label: "Overview"      },
  { to: "/fleet",      label: "Fleet"         },
  { to: "/continuity", label: "Continuity"    },
  { to: "/evidence",   label: "Evidence Packs"},
  { to: "/alerts",     label: "Alerts"        },
  { to: "/access",     label: "Access & Audit"},
  { to: "/settings",   label: "Settings"      },
] as const;

const NAV_WIDTH = 220;

async function signOut(): Promise<void> {
  try {
    const { getSupabaseClient } = await import("../lib/supabase");
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
  } catch {
    // ignore
  }
  window.location.assign("/login");
}

export default function DashboardLayout(props: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { openCount } = useAlerts();
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function loadEmail(): Promise<void> {
      try {
        const { getSupabaseClient } = await import("../lib/supabase");
        const supabase = getSupabaseClient();
        const { data } = await supabase.auth.getSession();
        if (alive) setUserEmail(data?.session?.user?.email ?? null);
      } catch { /* ignore */ }
    }
    void loadEmail();
    return () => { alive = false; };
  }, []);

  const sidebar = (
    <nav
      aria-label="Dashboard navigation"
      style={{
        width: `${NAV_WIDTH}px`,
        flexShrink: 0,
        borderRight: "1px solid var(--border)",
        background: "var(--surface-soft)",
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
        paddingTop: "1.25rem",
        paddingBottom: "1rem",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: ".6rem", padding: "0 1rem .875rem", borderBottom: "1px solid var(--border)", marginBottom: ".5rem" }}>
        <img src="/VerityEdge_Favicon.png" alt="" width="28" height="28" style={{ borderRadius: "6px", flexShrink: 0 }} />
        <img src="/VerityEdge_Logo.png" alt="Verity Edge" height="17" style={{ objectFit: "contain" }} />
      </div>

      {/* Nav links */}
      <div style={{ flex: 1 }}>
        {NAV_ITEMS.map((item) => {
          const active = item.to === "/"
            ? location.pathname === "/"
            : location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          const badge = item.to === "/alerts" && openCount > 0 ? openCount : null;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: ".5rem .75rem",
                textDecoration: "none",
                background: active ? "var(--brand)" : "transparent",
                color: active ? "white" : "var(--text)",
                borderRadius: "6px",
                margin: "0 .5rem",
                fontWeight: active ? 600 : 400,
                fontSize: ".875rem",
                transition: "background .12s",
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--border-subtle)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
            >
              {item.label}
              {badge != null && (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 .35rem",
                  borderRadius: "99px",
                  fontSize: ".65rem",
                  fontWeight: 700,
                  background: active ? "rgba(255,255,255,.25)" : "#dc2626",
                  color: "white",
                  lineHeight: 1,
                }}>
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* User + sign-out */}
      <div style={{ borderTop: "1px solid var(--border)", padding: ".75rem .5rem .5rem", marginTop: ".5rem" }}>
        {userEmail && (
          <div style={{ padding: ".25rem .75rem .5rem", fontSize: ".72rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={userEmail}>
            {userEmail}
          </div>
        )}
        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".4rem",
            width: "100%",
            padding: ".5rem .75rem",
            background: "transparent",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: ".82rem",
            color: "var(--muted)",
            fontWeight: 500,
            transition: "background .12s, color .12s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#991b1b"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--muted)"; }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </div>
    </nav>
  );

  return (
    <>
      <style>{`
        @media (max-width: 680px) {
          .ve-sidebar-static { display: none !important; }
          .ve-hamburger { display: flex !important; }
        }
        @media (min-width: 681px) {
          .ve-sidebar-static { display: flex !important; }
          .ve-hamburger { display: none !important; }
          .ve-sidebar-overlay { display: none !important; }
        }
      `}</style>

      {/* Mobile overlay */}
      {open && (
        <div
          className="ve-sidebar-overlay"
          onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 40 }}
        />
      )}

      {/* Mobile drawer */}
      {open && (
        <div style={{ position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 50, display: "flex" }}>
          {sidebar}
        </div>
      )}

      <div style={{ display: "flex", minHeight: "100vh" }}>
        {/* Static sidebar (desktop) */}
        <div className="ve-sidebar-static" style={{ display: "flex" }}>
          {sidebar}
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Mobile topbar */}
          <div
            className="ve-hamburger"
            style={{
              display: "none",
              alignItems: "center",
              gap: ".75rem",
              padding: ".75rem 1rem",
              borderBottom: "1px solid var(--border)",
              background: "var(--surface-soft)",
            }}
          >
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setOpen(true)}
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: ".25rem", color: "var(--text)", display: "flex" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <img src="/VerityEdge_Logo.png" alt="Verity Edge" height="16" style={{ objectFit: "contain" }} />
          </div>

          <main style={{ flex: 1, padding: "var(--gutter)", overflowY: "auto" }}>
            {props.children}
          </main>
        </div>
      </div>
    </>
  );
}
